import { useEffect, useRef, type ReactNode } from "react";
import { prefersReducedMotion } from "@/hooks/useInView";

interface Props {
  children: ReactNode;
  className?: string;
  /** Pixels of travel per viewport-height of scroll; negative drifts against the scroll. */
  speed?: number;
}

/**
 * Scroll-linked parallax. Offsets its content by how far it sits from the
 * centre of the nearest scrolling ancestor, so it drifts at a different rate
 * to the content around it. Idle (no listener) when off-screen or reduced-motion.
 */
export function Parallax({ children, className, speed = 40 }: Props) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    let reduced = prefersReducedMotion();
    let scroller: HTMLElement | Window = window;
    for (let p = el.parentElement; p; p = p.parentElement) {
      const oy = getComputedStyle(p).overflowY;
      if (oy === "auto" || oy === "scroll") {
        scroller = p;
        break;
      }
    }
    let raf = 0;
    let visible = true;
    const update = () => {
      raf = 0;
      if (reduced) return;
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      const offset = ((r.top + r.height / 2 - vh / 2) / vh) * speed;
      el.style.transform = `translate3d(0, ${offset.toFixed(1)}px, 0)`;
    };
    const onScroll = () => {
      if (visible && !reduced && !raf) raf = requestAnimationFrame(update);
    };
    const mq = typeof window.matchMedia === "function" ? window.matchMedia("(prefers-reduced-motion: reduce)") : null;
    const onMotionChange = (e: MediaQueryListEvent) => {
      reduced = e.matches;
      if (reduced) {
        if (raf) cancelAnimationFrame(raf);
        raf = 0;
        el.style.transform = "";
      } else onScroll();
    };
    mq?.addEventListener("change", onMotionChange);
    const io = new IntersectionObserver(([e]) => {
      visible = e.isIntersecting;
      if (visible) onScroll();
    });
    io.observe(el);
    update();
    scroller.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      scroller.removeEventListener("scroll", onScroll);
      mq?.removeEventListener("change", onMotionChange);
      io.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [speed]);

  return (
    <div ref={ref} className={className} style={{ willChange: "transform" }}>
      {children}
    </div>
  );
}
